import React, { useState } from 'react';
import { 
  CheckCircle, Users, Target, TrendingUp, Gift, Clock, 
  ArrowRight, Shield, Sparkles, ChevronRight, Send,
  User, Building, Mail, Phone, MapPin, Briefcase, Calendar,
  DollarSign
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import './FoundingVendorOffer.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stripe Payment Link
const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/4gMcN548NbhZ9Obe7jbfO01";

// Pollen mascot
const POLLEN_BEE_IMG = "https://customer-assets.emergentagent.com/job_bee-booking/artifacts/k8zgjyqb_Pollen%20-%20bHyyve%20Mascot.png";

// Industry options
const INDUSTRIES = [
  "General Contracting",
  "Electrical",
  "Plumbing",
  "Roofing",
  "HVAC",
  "Moving Services",
  "Event Management",
  "Cafés & Restaurants",
  "Bakeries",
  "Catering",
  "Professional Services",
  "Cleaning Services",
  "Landscaping",
  "Auto Services",
  "Beauty & Wellness",
  "Photography & Videography",
  "IT & Tech Services",
  "Financial Services",
  "Legal Services",
  "Health & Fitness",
  "Pet Services",
  "Home Renovation",
  "Interior Design",
  "Security Services",
  "Transportation",
  "Other"
];

// FAQ Data
const faqs = [
  { 
    q: "How long does approval take?", 
    a: "Most applications are reviewed within a few business days." 
  },
  { 
    q: "Is this contract-based?", 
    a: "No long-term commitment beyond the 1-year Founding Vendor term." 
  },
  { 
    q: "What is the First Order Guarantee Program?", 
    a: "Eligible vendors receive structured support designed to help generate their first buyer opportunity. Conditions apply." 
  }
];

const FoundingVendorOffer = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    businessName: '',
    email: '',
    phone: '',
    industry: '',
    serviceArea: '',
    yearsInBusiness: '',
    employeeCount: '',
    acceptingCustomers: '',
    orderTimeline: '',
    website: '',
    averageOrderSize: '',
    additionalNotes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.fullName || !formData.businessName || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!formData.industry || !formData.serviceArea || !formData.yearsInBusiness) {
      toast.error('Please complete all business details');
      return;
    }
    
    if (!formData.acceptingCustomers || !formData.orderTimeline) {
      toast.error('Please answer the qualification questions');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/founding-vendor-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit application');
      }
      
      setSubmitted(true);
      toast.success('Application submitted! Redirecting to payment...');
      
      // Redirect to Stripe payment page after short delay
      setTimeout(() => {
        window.location.href = STRIPE_PAYMENT_LINK;
      }, 2000);
      
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    const form = document.getElementById('application-form');
    if (form) {
      form.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Success Screen - Redirecting to Payment
  if (submitted) {
    return (
      <div className="fvo-page fvo-success-page" data-testid="fvo-success">
        <div className="fvo-container">
          <div className="fvo-success-content">
            <div className="fvo-success-bee">
              <img src={POLLEN_BEE_IMG} alt="Pollen" className="fvo-pollen-img" />
            </div>
            <h1>Application Submitted!</h1>
            <p>
              Thank you for starting your investment in the bHyyve ecosystem!
              You'll be redirected to complete your payment in a moment...
            </p>
            <div className="fvo-promo-code-success">
              <Gift size={20} />
              <span>Use Promo Code: <strong>FOUNDER2026</strong> at checkout</span>
            </div>
            <div className="fvo-redirect-notice">
              <div className="fvo-spinner"></div>
              <span>Redirecting to secure payment...</span>
            </div>
            <div className="fvo-payment-info">
              <DollarSign size={20} />
              <span>90% OFF — Your investment in business growth</span>
            </div>
            <a href={STRIPE_PAYMENT_LINK} className="fvo-manual-pay-btn">
              Click here if not redirected automatically
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fvo-page" data-testid="founding-vendor-offer">
      {/* Header */}
      <header className="fvo-header">
        <div className="fvo-container">
          <div className="fvo-header-content">
            <a href="/" className="fvo-logo-link">
              <img src="/logo.svg" alt="bHyyve Logo" className="fvo-logo" />
              <span className="fvo-logo-text">bHyyve</span>
            </a>
            <a href="/" className="fvo-home-link">
              <ArrowRight size={18} className="fvo-arrow-left" />
              Visit Main Website
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="fvo-hero">
        <div className="fvo-container">
          <div className="fvo-hero-content">
            <div className="fvo-hero-text">
              <h1>
                Get Qualified Buyers Finding <span className="fvo-highlight">Your</span> Business
              </h1>
              <h2>Apply to Become a Founding Vendor on bHyyve</h2>
              <p className="fvo-hero-desc">
                Customers are actively searching for businesses like yours right now. 
                bHyyve connects approved vendors with real buyers ready to place purchase orders 
                — without expensive ads or lead chasing.
              </p>
              
              <div className="fvo-offer-box">
                <div className="fvo-offer-label">
                  <Sparkles size={16} />
                  Limited-time Founding Vendor Offer
                </div>
                <div className="fvo-offer-price">
                  <span className="fvo-discount">90% OFF</span>
                  <span className="fvo-original-price">$299</span>
                  <span className="fvo-duration">1-Year Subscription</span>
                </div>
                <div className="fvo-promo-code">
                  <Gift size={18} />
                  <span>Promo Code: <strong>FOUNDER2026</strong></span>
                </div>
                <div className="fvo-investment-note">
                  <DollarSign size={16} />
                  <span>An investment that pays for itself — generate income by joining the bHyyve ecosystem</span>
                </div>
              </div>
              
              <Button className="fvo-hero-cta" onClick={scrollToForm}>
                Claim 90% Off Now
                <ArrowRight size={20} />
              </Button>
            </div>
            <div className="fvo-hero-visual">
              <img src={POLLEN_BEE_IMG} alt="Pollen - bHyyve Assistant" className="fvo-hero-bee" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="fvo-why-section">
        <div className="fvo-container">
          <h2>Why Business Owners Join bHyyve</h2>
          <div className="fvo-benefits-grid">
            <div className="fvo-benefit-card">
              <Target size={32} />
              <h3>Receive Buyer Requests</h3>
              <p>Stop chasing leads — let qualified buyers come to you</p>
            </div>
            <div className="fvo-benefit-card">
              <Users size={32} />
              <h3>Get Matched</h3>
              <p>Connect with customers in your industry and service area</p>
            </div>
            <div className="fvo-benefit-card">
              <TrendingUp size={32} />
              <h3>Reduce Marketing Spend</h3>
              <p>Increase order opportunities without expensive ads</p>
            </div>
            <div className="fvo-benefit-card">
              <Gift size={32} />
              <h3>Simple Onboarding</h3>
              <p>Get started quickly with our assistant Pollen</p>
            </div>
          </div>
          
          <div className="fvo-industries">
            <p>
              <strong>Industries we support include (but are not limited to):</strong>
            </p>
            <p className="fvo-industry-list">
              General Contracting, Electrical, Plumbing, Roofing, Moving, Event Management, 
              Cafés, Bakeries, Professional Services, and more.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="fvo-benefits-section">
        <div className="fvo-container">
          <h2>Founding Vendor Benefits</h2>
          <div className="fvo-checklist">
            <div className="fvo-check-item">
              <CheckCircle size={24} />
              <span>1-Year Vendor Software Subscription</span>
            </div>
            <div className="fvo-check-item">
              <CheckCircle size={24} />
              <span>Buyer Matching & Order Opportunities</span>
            </div>
            <div className="fvo-check-item">
              <CheckCircle size={24} />
              <span>Vendor Onboarding & Training</span>
            </div>
            <div className="fvo-check-item">
              <CheckCircle size={24} />
              <span>Access to our First Order Guarantee Program*</span>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="fvo-form-section" id="application-form">
        <div className="fvo-container">
          <div className="fvo-form-header">
            <h2>Apply Now — It Takes Less Than 2 Minutes</h2>
            <p>
              Fill out the form below to apply. Once submitted, our team will review 
              your information and contact you to complete approval.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="fvo-form" data-testid="fvo-application-form">
            {/* Required Information */}
            <div className="fvo-form-group">
              <h3>
                <User size={20} />
                Required Information
              </h3>
              <div className="fvo-form-row">
                <div className="fvo-field">
                  <label>Full Name *</label>
                  <Input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    placeholder="John Smith"
                    required
                    data-testid="input-fullName"
                  />
                </div>
                <div className="fvo-field">
                  <label>Business Name *</label>
                  <Input
                    type="text"
                    value={formData.businessName}
                    onChange={(e) => handleInputChange('businessName', e.target.value)}
                    placeholder="Your Business Inc."
                    required
                    data-testid="input-businessName"
                  />
                </div>
              </div>
              <div className="fvo-form-row">
                <div className="fvo-field">
                  <label>Business Email Address *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="you@business.com"
                    required
                    data-testid="input-email"
                  />
                </div>
                <div className="fvo-field">
                  <label>Phone Number *</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    required
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>

            {/* Business Details */}
            <div className="fvo-form-group">
              <h3>
                <Building size={20} />
                Business Details
              </h3>
              <div className="fvo-form-row">
                <div className="fvo-field">
                  <label>Industry / Service Type *</label>
                  <select
                    value={formData.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    required
                    data-testid="select-industry"
                  >
                    <option value="">Select your industry</option>
                    {INDUSTRIES.map(ind => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>
                <div className="fvo-field">
                  <label>Primary Service Area *</label>
                  <Input
                    type="text"
                    value={formData.serviceArea}
                    onChange={(e) => handleInputChange('serviceArea', e.target.value)}
                    placeholder="City / Region"
                    required
                    data-testid="input-serviceArea"
                  />
                </div>
              </div>
              <div className="fvo-form-row">
                <div className="fvo-field">
                  <label>Years in Business *</label>
                  <select
                    value={formData.yearsInBusiness}
                    onChange={(e) => handleInputChange('yearsInBusiness', e.target.value)}
                    required
                    data-testid="select-yearsInBusiness"
                  >
                    <option value="">Select</option>
                    <option value="0-1">0–1 years</option>
                    <option value="2-5">2–5 years</option>
                    <option value="5-10">5–10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>
                <div className="fvo-field">
                  <label>Number of Employees / Contractors *</label>
                  <select
                    value={formData.employeeCount}
                    onChange={(e) => handleInputChange('employeeCount', e.target.value)}
                    required
                    data-testid="select-employeeCount"
                  >
                    <option value="">Select</option>
                    <option value="1">Just me</option>
                    <option value="2-5">2–5</option>
                    <option value="6-10">6–10</option>
                    <option value="11-25">11–25</option>
                    <option value="26-50">26–50</option>
                    <option value="50+">50+</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Readiness & Qualification */}
            <div className="fvo-form-group">
              <h3>
                <Briefcase size={20} />
                Readiness & Qualification
              </h3>
              <div className="fvo-form-row">
                <div className="fvo-field">
                  <label>Are you currently accepting new customers? *</label>
                  <div className="fvo-radio-group">
                    <label className={`fvo-radio ${formData.acceptingCustomers === 'yes' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="acceptingCustomers"
                        value="yes"
                        checked={formData.acceptingCustomers === 'yes'}
                        onChange={(e) => handleInputChange('acceptingCustomers', e.target.value)}
                        data-testid="radio-accepting-yes"
                      />
                      <span>Yes</span>
                    </label>
                    <label className={`fvo-radio ${formData.acceptingCustomers === 'no' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="acceptingCustomers"
                        value="no"
                        checked={formData.acceptingCustomers === 'no'}
                        onChange={(e) => handleInputChange('acceptingCustomers', e.target.value)}
                        data-testid="radio-accepting-no"
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>
                <div className="fvo-field">
                  <label>How soon are you looking to receive new orders? *</label>
                  <select
                    value={formData.orderTimeline}
                    onChange={(e) => handleInputChange('orderTimeline', e.target.value)}
                    required
                    data-testid="select-orderTimeline"
                  >
                    <option value="">Select</option>
                    <option value="immediately">Immediately</option>
                    <option value="30-days">Within 30 Days</option>
                    <option value="60-days">60+ Days</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Optional Fields */}
            <div className="fvo-form-group fvo-optional">
              <h3>
                <Calendar size={20} />
                Optional (Helps Speed Up Approval)
              </h3>
              <div className="fvo-form-row">
                <div className="fvo-field">
                  <label>Business Website or Social Media Link</label>
                  <Input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://yourbusiness.com"
                    data-testid="input-website"
                  />
                </div>
                <div className="fvo-field">
                  <label>Average Order Size</label>
                  <select
                    value={formData.averageOrderSize}
                    onChange={(e) => handleInputChange('averageOrderSize', e.target.value)}
                    data-testid="select-averageOrderSize"
                  >
                    <option value="">Select (optional)</option>
                    <option value="under-100">Under $100</option>
                    <option value="100-500">$100 - $500</option>
                    <option value="500-1000">$500 - $1,000</option>
                    <option value="1000-5000">$1,000 - $5,000</option>
                    <option value="5000+">$5,000+</option>
                  </select>
                </div>
              </div>
              <div className="fvo-field fvo-full-width">
                <label>Additional Notes or Questions</label>
                <textarea
                  value={formData.additionalNotes}
                  onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                  placeholder="Tell us more about your business or ask any questions..."
                  rows={4}
                  data-testid="textarea-notes"
                />
              </div>
            </div>

            {/* Consent & Submit */}
            <div className="fvo-consent">
              <p>
                By submitting this form, you agree to be contacted by bHyyve regarding 
                your application, onboarding, and vendor opportunities. We respect your 
                privacy and will never sell your information.
              </p>
            </div>

            <Button 
              type="submit" 
              className="fvo-submit-btn"
              disabled={isSubmitting}
              data-testid="submit-application"
            >
              {isSubmitting ? (
                'Submitting...'
              ) : (
                <>
                  <Send size={20} />
                  Apply to Become a Founding Vendor
                </>
              )}
            </Button>
          </form>
        </div>
      </section>

      {/* What Happens After Section */}
      <section className="fvo-process-section">
        <div className="fvo-container">
          <h2>What Happens After You Apply?</h2>
          <div className="fvo-process-steps">
            <div className="fvo-process-step">
              <div className="fvo-process-num">1</div>
              <h3>Application Review</h3>
              <p>Our team verifies your business details to ensure buyer quality.</p>
            </div>
            <div className="fvo-process-connector"></div>
            <div className="fvo-process-step">
              <div className="fvo-process-num">2</div>
              <h3>Onboarding with Pollen</h3>
              <p>Pollen helps you complete setup, training, and profile optimization.</p>
            </div>
            <div className="fvo-process-connector"></div>
            <div className="fvo-process-step">
              <div className="fvo-process-num">3</div>
              <h3>Start Receiving Buyer Matches</h3>
              <p>Approved vendors begin receiving order opportunities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Limited Availability */}
      <section className="fvo-urgency-section">
        <div className="fvo-container">
          <div className="fvo-urgency-box">
            <Clock size={32} />
            <div>
              <h3>Limited Availability</h3>
              <p>
                Founding Vendor pricing is limited and subject to approval. 
                Once spots are filled, pricing returns to standard rates.
              </p>
            </div>
            <Button className="fvo-urgency-cta" onClick={scrollToForm}>
              Apply Now
              <ArrowRight size={18} />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="fvo-faq-section">
        <div className="fvo-container">
          <h2>Frequently Asked Questions</h2>
          <div className="fvo-faq-list">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`fvo-faq-item ${openFaq === index ? 'open' : ''}`}
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
              >
                <div className="fvo-faq-question">
                  <span>{faq.q}</span>
                  <ChevronRight className={`fvo-faq-arrow ${openFaq === index ? 'rotated' : ''}`} size={20} />
                </div>
                {openFaq === index && (
                  <div className="fvo-faq-answer">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="fvo-disclaimer">
        <div className="fvo-container">
          <p>
            *First Order Guarantee Program subject to eligibility, onboarding completion, 
            and platform participation requirements.
          </p>
        </div>
      </section>

      {/* Sticky CTA */}
      <div className="fvo-sticky-cta">
        <Button className="fvo-sticky-btn" onClick={scrollToForm}>
          Claim 90% Off — Limited Time Only
        </Button>
      </div>
    </div>
  );
};

export default FoundingVendorOffer;
